import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListSubheader,
  ListItemText,
  Collapse,
  Grid,
  Portal,
  IconButton,
  Fade,
  Paper,
  LinearProgress,
  ListItemIcon
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import useJobsContext from 'src/hooks/useJobsContext';
import { blueGrey, grey } from '@mui/material/colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const StyledLinearProgress = styled(LinearProgress)(() => ({
  height: '1px',
  backgroundColor: 'unset',
  borderBottom: '0px solid rgb(167, 202, 237)',
  '& .MuiLinearProgress-bar1Indeterminate': {
    height: '1px'
  }
}));

const useStyles = makeStyles((theme) => ({
  loadingWidget: {
    // border: '1px solid #0000001f',
    position: 'absolute',
    bottom: 20,
    left: 30,
    zIndex: theme.zIndex.tooltip
    // backgroundColor: '#fff'
  },
  loadingWidgetList: {
    paddingBottom: '0',
    '& .MuiListSubheader-root': {
      paddingRight: '0'
    }
  },
  loadingWidgetListHeader: {
    backgroundColor: theme.palette.primary.dark,
    color: 'white'
  },
  headerIcon: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  headerButton: {
    color: 'inherit',
    padding: '6px'
  },
  wrapperOverflow: {
    overflowY: 'auto !important'
  },
  wrapper: {
    width: '100%',
    overflow: 'hidden'
  },
  list: {
    maxHeight: '500px'
  },
  noGutters: {
    padding: 0
  }
}));

const icon = {
  1: <CheckBoxIcon size={20} color="secondary" />,
  2: <HighlightOffRoundedIcon size={20} color="error" />
};

const ProgressWidget = () => {
  const theme = useTheme();
  const classes = useStyles();
  const { jobs, refreshJobs } = useJobsContext();
  const [activeJobs, setActiveJobs] = useState({
    timestamp: null,
    rows: []
  });
  const [show, setShow] = useState(false);
  const [inProgress, setInProgress] = useState([]);
  const [open, setOpen] = useState(true);
  const container = useRef(null);
  const interval = useRef();
  const navigate = useNavigate();
  const handleOnIdle = () => {
    clearInterval(interval.current);
    interval.current = null;
  };

  const handleOnActive = () => {
    if (!interval.current) {
      interval.current = setInterval(refreshJobs, 5000);
    }
  };

  const headerTitle = useMemo(() => {
    if (inProgress.length > 0) {
      return `Processing ${inProgress.length} item${
        inProgress.length > 1 ? 's' : ''
      }`;
    }
    return `${activeJobs.rows.length} job${
      activeJobs.rows.length > 1 ? 's' : ''
    } completed`;
  }, [inProgress.length, activeJobs.rows.length]);

  const closeWidget = () => {
    setShow(false);
    setTimeout(() => {
      setActiveJobs({
        timestamp: +new Date(),
        rows: []
      });
    }, 200);
  };

  const handleItemClick = (jobId) => {
    navigate(`/app/reporting/logs?selected=${jobId}`);
  };

  useEffect(() => {
    if (inProgress.length > 0) {
      handleOnActive();
    } else {
      handleOnIdle();
    }
    return () => {
      handleOnIdle();
    };
  }, [inProgress]);

  useEffect(() => {
    if (jobs) {
      let actives = activeJobs.rows;
      jobs.forEach((job) => {
        const findIndex = actives.findIndex(({ id }) => id === job.id);
        if (job.status === 0 && findIndex === -1) {
          actives = [...actives, job];
        } else if (findIndex > -1) {
          actives[findIndex] = job;
        }
      });
      setActiveJobs({
        timestamp: +new Date(),
        rows: actives
      });
    }
  }, [jobs]);

  useEffect(() => {
    const progress = activeJobs.rows.filter((job) => job.status === 0);
    setInProgress(progress);
  }, [activeJobs.timestamp]);

  useEffect(() => {
    setShow(activeJobs.rows.length > 0);
  }, [activeJobs.rows.length]);

  return (
    <Portal container={container.current}>
      <Fade in={show}>
        <Paper
          className={classes.loadingWidget}
          elevation={2}
          sx={{
            minWidth: '300px'
            // minWidth: open ? '400px' : '300px'
          }}
        >
          <List
            dense
            className={classes.loadingWidgetList}
            subheader={
              <ListSubheader className={classes.loadingWidgetListHeader}>
                <Grid container spacing={0}>
                  {headerTitle}
                  <Box flexGrow={1} />
                  <Box paddingRight={1}>
                    <IconButton
                      className={classes.headerButton}
                      aria-label={open ? 'Expand' : 'Minimize'}
                      onClick={() => {
                        setOpen(!open);
                      }}
                      size="large"
                    >
                      {open ? <ExpandMore /> : <ExpandLess />}
                    </IconButton>
                    {inProgress.length === 0 && (
                      <IconButton
                        aria-label="Close"
                        className={classes.headerButton}
                        onClick={closeWidget}
                        size="large"
                      >
                        <CloseRoundedIcon />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              </ListSubheader>
            }
          >
            <Collapse
              in={open}
              timeout="auto"
              unmountOnExit
              className={clsx({
                [classes.wrapper]: true,
                [classes.wrapperOverflow]: open
              })}
            >
              <Box className={classes.list}>
                {activeJobs.rows.map((job) => (
                  <Box key={job.id}>
                    <ListItem button onClick={() => handleItemClick(job.id)}>
                      <ListItemIcon>
                        {icon[job.status] ? (
                          icon[job.status]
                        ) : (
                          <InfoOutlinedIcon />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={job.description}
                        secondary={`${job.subdomain}.zendesk.com`}
                      />
                    </ListItem>
                    <Box
                      width="100%"
                      sx={{
                        borderBottom: `1px solid ${
                          theme.palette.mode === 'light'
                            ? grey[200]
                            : blueGrey[800]
                        }`
                      }}
                    >
                      {job.status === 0 ? (
                        <StyledLinearProgress sx={{ width: '100%' }} />
                      ) : (
                        <Box height="1px" />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </List>
        </Paper>
      </Fade>
    </Portal>
  );
};

export default ProgressWidget;
